import { makeStyles, MuiThemeProvider } from "@material-ui/core";
import React from "react";
import Grid from "@material-ui/core/Grid";
import { chain, chunk, orderBy, throttle } from "lodash";
import PieChart from "../PieChart";
import BarChart from "../BarChart";
import themeDashboard from "../../shared/themeDashboard";
import { consts } from "../../../common/consts";
import api from "../../../store/api";
import eventNames from "../../../store/signalr/eventNames";
import { addTaskForEvent, removeTaskForEvent } from "../../../store/signalr/tasksForEvents";
import { IUserType } from "../types/dashboardTypes"
import IAuthState from "../../../interfaces/IAuthState";
import KPIWidget from './KPIWidget';
import { IAgedCareDashboardCombinedResponse, IAgedCareDashboardResponse, IDashboardBarChartResponse, IPieCharObj, IKPIData, IBarChartDayoFWeekObj, ICalloutPerServiceDayStatResponse } from './types/agedCareDashboardTypes';
import { createKPIData } from './agedCareDashboardHelpers';
import BarChartWithTrendLine from "../BarChartWithTrendLine";

interface AgedCareDashboardWidgetProps {
    startDate: string,
    endDate: string,
    selectedLocations: string[],
    authStore: IAuthState,
    userRoles: IUserType
};

const getDuplicates = (arr: string[]): string[] => arr.filter((e, i, a) => a.indexOf(e) !== i);
const createUniqueName = (duplicates: string[], name: string, index:number): string => {
    if(duplicates.length === 0)
        return name;
    
    if(duplicates.includes(name))
        return name + ' '.repeat(index);
        
    return name;
}

const createPayload = (startDate: string, endDate: string, selectedLocations: string[]) => {
    return {
        locationIds: selectedLocations,
        requestedStartDate: startDate,
        requestedEndDate: endDate
    };
}

const NoData = () => {
    return <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300 }}><h2>No data found !</h2></div>
}

const LoadingData = () => {
    return <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300 }}><h2>Loading data... </h2></div>
}

const useStyles = makeStyles((theme) => ({
    piChartContainer: {
        height: "348px",
        border: "1px solid rgba(255, 255, 255, 0.17)",
        borderLeft: "0px",
    }
}));


const typeListResponseToPieChartObj = (dashboardBarChartTypeListResponse: IDashboardBarChartResponse[]): IPieCharObj[] =>
    orderBy(dashboardBarChartTypeListResponse.map((el) => ({
        id: el.name,
        label: el.name.toLowerCase().includes('incident') ? 'Medical incident' : 'Overflow',
        value: el.value
    })), ['label'], ['desc']);

const dashboardTopDestinationsResponseToPieChartObj = (dashboardTopDestinationsResponse: IAgedCareDashboardResponse[]): IPieCharObj[] =>{
    const duplicatedNames =  getDuplicates(dashboardTopDestinationsResponse.map((el) => (el.name??'')));
    return dashboardTopDestinationsResponse.map((el, index) => ({
        id: createUniqueName(duplicatedNames, (el.name ?? ''), index),
        label: el.name ?? '',
        value: el.recordCount ? el.recordCount : 0
    }));
}
const dashboardReasonsResponseToPieChartObj = (dashboardReasonsResponse: IAgedCareDashboardResponse[]): IPieCharObj[] =>{
    const duplicatedNames =  getDuplicates(dashboardReasonsResponse.map((el) => (el.name??'')));
    return dashboardReasonsResponse.map((el, index) => ({
        id: createUniqueName(duplicatedNames, (el.name ?? ''), index),
        label: el.name ?? '',
        value: el.recordCount ? el.recordCount : 0
    }));
}
const timesByDayOfAWeekStatsResponseToBarChart = (dashboardTimesByDayOfAWeekStatsResponse: IAgedCareDashboardResponse[]): any[] => {
    return chunk(dashboardTimesByDayOfAWeekStatsResponse, dashboardTimesByDayOfAWeekStatsResponse.length / 7).map((item: any[]) => {
        return {
            ...chain(item)
                .keyBy('description')
                .mapValues('recordCount')
                .value(), Weekday: item[0].name
        }
    })
}

const componentTaskId = "agedCareDashboardWidget";

const AgedCareDashboardWidget: React.FC<AgedCareDashboardWidgetProps> = ({
    startDate,
    endDate,
    selectedLocations,
    authStore,
    userRoles
}) => {
    const [kpiData, setKPIData] = React.useState<IKPIData>(createKPIData(userRoles));
    const [combinedStatLoading, setCombinedStatLoading] = React.useState<boolean>(false);
    const [calloutTypeList, setCalloutTypeList] = React.useState<IPieCharObj[]>([]);
    const [calloutsByLocation, setCalloutsByLocation] = React.useState<IPieCharObj[]>([]);
    const [reasonsForCallout, setReasonsForCallout] = React.useState<IPieCharObj[]>([]);
    const [calloutTimesbyDay, setCalloutTimesbyDay] = React.useState<IBarChartDayoFWeekObj[]>([]);

    const [calloutsAttended, setCalloutsAttended] = React.useState<any[]>([]);
    const [calloutsAttendedLoading, setCalloutsAttendedLoading] = React.useState<boolean>(false);

    const classes = useStyles();

    const isCalloutsByLocationVisible = selectedLocations?.length !== 1;

    const whenCalloutEventDo = () => {
        throttleRefreshCharts(createPayload(startDate, endDate, selectedLocations));
    }

    React.useEffect(() => {
        addTaskForEvent(eventNames.refreshCallouts, componentTaskId, whenCalloutEventDo);
        return () => {
            removeTaskForEvent(eventNames.refreshCallouts, componentTaskId);
        }
    }, [whenCalloutEventDo]);

    React.useEffect(() => {
        setCombinedStatLoading(true);
        setCalloutsAttendedLoading(true);
        throttleRefreshCharts(createPayload(startDate, endDate, selectedLocations));

    }, [startDate, endDate, selectedLocations])

    const throttleRefreshCharts = React.useMemo(() => throttle(async (reqData: any): Promise<void> => {
        await refreshCharts(reqData);
    }, 2000), [])



    const refreshCharts = async (reqData: any): Promise<void> => {

        if (!userRoles.IsAdmin && !userRoles.CanViewDashboardAsSMA && authStore.data?.location)
            reqData.locationIds = [authStore.data.location];

        const combinedStatsPromise = api.getCalloutCombinedStats("", reqData);
        const calloutPerServicePromise = api.getCalloutPerServiceDayStat("", reqData);
        try {

            const res: IAgedCareDashboardCombinedResponse = await combinedStatsPromise;
            if (res.errorRaised)
                throw res.errorMessage ?? 'Something wrong';
            setKPIData(createKPIData(userRoles, res.model.agedCareDashboardResponse));
            setCalloutTypeList(typeListResponseToPieChartObj(res.model.dashboardBarChartTypeListResponse));
            setCalloutsByLocation(dashboardTopDestinationsResponseToPieChartObj(res.model.dashboardTopDestinationsResponse));
            setReasonsForCallout(dashboardReasonsResponseToPieChartObj(res.model.dashboardReasonsResponse));
            setCalloutTimesbyDay(timesByDayOfAWeekStatsResponseToBarChart(res.model.dashboardTimesByDayOfAWeekStatsResponse));
            setCombinedStatLoading(false);

        } catch (error) {
            setKPIData(createKPIData(userRoles));
            setCalloutTypeList([]);
            setCalloutsByLocation([]);
            setReasonsForCallout([]);
            setCalloutTimesbyDay([]);
            console.error(error);
            setCombinedStatLoading(false);
        }

        try {

            const res: ICalloutPerServiceDayStatResponse = await calloutPerServicePromise;
            if (res.errorRaised)
                throw res.errorMessage ?? 'Something wrong';
            setCalloutsAttended(res.model);
            setCalloutsAttendedLoading(false);

        } catch (error) {
            setCalloutsAttended([]);
            console.error(error);
            setCalloutsAttendedLoading(false);
        }

    };
    return (
        <>
            <MuiThemeProvider theme={themeDashboard}>
                <Grid container>
                    <KPIWidget
                        kpiData={kpiData}
                    />
                    {/* Graphs */}

                    <Grid item xs={12} md={12} lg={12}>
                        <div
                            style={{
                                height: "348px",
                                borderTop: "1px solid rgba(255, 255, 255, 0.17)",
                            }}
                        >
                            {calloutsAttendedLoading ? <LoadingData /> :
                                <div>
                                    {calloutsAttended.length > 0 ?
                                        <BarChartWithTrendLine
                                            data={calloutsAttended}
                                            title="Callouts Attended"
                                            colors={['#4B753D']} />
                                        : <NoData />
                                    }
                                </div>
                            }
                        </div>
                    </Grid>

                    <Grid item xs={12} md={6} lg={6} xl={6}>
                        <div className={classes.piChartContainer}>
                            {combinedStatLoading ? <LoadingData /> :
                                <div>
                                    {calloutTypeList.length > 0 ?
                                        <PieChart
                                            chartData={calloutTypeList}
                                            colors={['#D89629', '#973636']}
                                            title="Callout Type" />
                                        : <NoData />
                                    }
                                </div>}
                        </div>
                    </Grid>
                    {isCalloutsByLocationVisible &&
                        <Grid item xs={12} md={6} lg={6} xl={6}>
                            <div className={classes.piChartContainer}>
                                {combinedStatLoading ? <LoadingData /> :
                                    <div>
                                        {calloutsByLocation.length > 0 ?
                                            <PieChart
                                                chartData={calloutsByLocation}
                                                colors={consts.GRAPH_COLORS}
                                                title="Callouts By Location" />
                                            : <NoData />
                                        }
                                    </div>}
                            </div>
                        </Grid>}
                    <Grid item xs={12} md={6} lg={6} xl={6}>
                        <div className={classes.piChartContainer}>
                            {combinedStatLoading ? <LoadingData /> :
                                <div>
                                    {reasonsForCallout.length > 0 ?
                                        <PieChart
                                            chartData={reasonsForCallout}
                                            colors={consts.GRAPH_COLORS}
                                            title="Reason For Callout" />
                                        : <NoData />
                                    }
                                </div>
                            }
                        </div>
                    </Grid>
                    <Grid item xs={12} md={6} lg={6} xl={6}>
                        {combinedStatLoading ? <LoadingData /> :
                            <div className={classes.piChartContainer}>
                                {calloutTimesbyDay.length > 0 ?
                                    <BarChart
                                        barChartData={calloutTimesbyDay}
                                        colors={consts.GRAPH_COLORS_BAR2}
                                        title="Callout Times by Day of Week" />
                                    : <NoData />
                                }
                            </div>
                        }
                    </Grid>
                </Grid>
            </MuiThemeProvider>
        </>
    );
}

export default AgedCareDashboardWidget;